/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { CloseIcon } from 'src/icons';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import moment, { duration } from 'moment';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';
import RHFEditor from '../elements/Editor/RHFEditor';
import RHFEmailsChips from '../elements/EmailsChips/RHFEmailsChips';
import RHFInput from '../elements/Input/RHFInput';
import RHFSelect from '../elements/Select/RHFSelect';
import RHFDatePicker from '../elements/CustomDatePicker/RHFDatePicker';
import RHFTimePicker from '../elements/CustomTimePicker/RHFTimePicker';
import { MailTemplateRequest } from './types';
import Loader from '../Loader';

const howOptions = [
  { label: 'Phone Interview', value: 'SCHEDULE_CLIENT_PHONE_INTERVIEW' },
  { label: 'Face 2 Face', value: 'SCHEDULE_CLIENT_FACE2FACE_INTERVIEW' },
  { label: 'Video Conference', value: 'SCHEDULE_CLIENT_VIDEOCONFERENCE_INTERVIEW' },
];

const timeZoneOptions = [
  { label: 'CST', value: 'CST' },
  { label: 'EST', value: 'EST' },
  { label: 'IST', value: 'IST' },
  { label: 'MST', value: 'MST' },
  { label: 'PST', value: 'PST' },
];

const durationOptions = [
  { label: '15min', value: '15min' },
  { label: '30min', value: '30min' },
  { label: '45min', value: '45min' },
  { label: '1hr', value: '1hr' },
  { label: '1hr 15min', value: '1hr 15min' },
  { label: '1hr 30min', value: '1hr 30min' },
  { label: '1hr 45min', value: '1hr 45min' },
  { label: '2hr', value: '2hr' },
];

export interface emailValues {
  to: Array<string>;
  cc?: Array<string> | null;
  subject: string;
  content: string;
}

export interface ScheduleClientInterviewFormValues extends emailValues {
  how: string;
  date: string;
  time: string;
  timeZone: string;
  duration: string;
  where: string;
  who: string;
}

const validationSchema = yup.object().shape({
  to: yup.array(yup.string().email()).required('Must have at least 1 email'),
  cc: yup.array(yup.string().email()).optional(),
  subject: yup.string().required('subject required').min(1, 'Subject required'),
  content: yup.string().required('email content required'),
  how: yup.string().required('mode is required'),
  date: yup.string().required('date is required'),
  time: yup.string().required('time is required'),
  timeZone: yup.string().required('timezone is required'),
  duration: yup.string().required('duration is required'),
  where: yup.string().required('location is required'),
  who: yup.string().required('interviewer is required'),
});

interface ScheduleClientInterviewProps {
  onSubmit: (data: emailValues) => void;
  onCancel: () => void;
  loading: boolean;
  defaultValues?: emailValues | undefined;
  jobOrderId?: number;
  candidateId: number;
  getMailTemplate: (data: MailTemplateRequest) => void;
  title?: string;
}

const ScheduleClientInterview = function ScheduleClientInterview(props: ScheduleClientInterviewProps) {
  const [loading, setLoading] = useState(false);
  const {
    jobOrderId,
    candidateId,
    getMailTemplate,
    onSubmit,
    defaultValues,
    onCancel,
    title,
  } = props;
  const resolver = useYupValidationResolver(validationSchema);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  const methods = useForm<ScheduleClientInterviewFormValues>({
    defaultValues,
    resolver,
  });

  const {
    handleSubmit,
    getValues,
  } = methods;

  useEffect(() => {
    // set the how value in the template
    methods.setValue('to', defaultValues?.to || []);
    methods.setValue('cc', defaultValues?.cc);
    methods.setValue('subject', defaultValues?.subject || '');
    getValueFromFormAndUpdateTemplate(
      '{{CLIENT_INTERVIEW_MODE}}',
      howOptions.find(({ value }) => howValue === value)?.label || '',
    );
  }, [defaultValues]);

  const howValue = getValues('how');

  const submitHandler: SubmitHandler<ScheduleClientInterviewFormValues> = async (data) => {
    setDisableSubmit(true);
    const {
      date = '',
      time = '',
    } = data;

    const interviewDate = date && moment(date).format('MM/DD/YYYY');
    const interviewTime = time && moment(time).format('hh:mm A');
    const request = {
      ...data,
      date: (interviewDate || ''),
      time: (interviewTime || ''),
    };
    await onSubmit(request);
    setDisableSubmit(false);
  };

  const replaceTemplateValues = (template: string, key: string, value: string) => (
    template.replaceAll(key, value)
  );

  const getValueFromFormAndUpdateTemplate = (dynamicKey: string, value: string) => {
    let modified = replaceTemplateValues(
      defaultValues?.content || '',
      dynamicKey,
      value
    );
    const {
      how,
      date,
      time,
      timeZone,
      duration,
      where,
      who,
    } = getValues();

    const dynamicKeys = {
      how: '{{CLIENT_INTERVIEW_MODE}}',
      date: '{{CLIENT_INTERVIEW_DATE}}',
      time: '{{CLIENT_INTERVIEW_TIME_SLOT}}',
      timeZone: '{{ZONE}}',
      duration: '{{CLIENT_DURATION}}',
      where: '{{CLIENT_INTERVIEW_LOCATION}}',
      who: '{{CLIENT_INTERVIEWER_NAME}}',
    };

    const valuesObj = {
      how,
      date,
      time,
      timeZone,
      duration,
      where,
      who,
    };

    Object.keys(dynamicKeys).map((key) => {
      const typedKey = key as keyof typeof dynamicKeys;
      if (valuesObj[typedKey]) {
        if (key === 'how') {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            howOptions.find(({ value }) => howValue === value)?.label || '',
          );
        } else if (key === 'date') {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            moment(valuesObj[typedKey]).format('MM/DD/YYYY'),
          );
        } else if (key === 'time') {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            moment(valuesObj[typedKey]).format('hh:mm A'),
          );
        } else {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            valuesObj[typedKey],
          );
        }
      }
    });

    methods.setValue('content', modified);
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitHandler)} className="bg-white  p-10 flex gap-8 justify-center items-center flex-col">
          {title && (
            <div className="flex justify-center items-center w-full text-[#28364C] font-medium text-base">
              {title}
            </div>
          )}

          <div className="flex w-full p-4 flex-col gap-10">
            <div className="w-full flex-shrink-0">

              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                  <RHFSelect
                    control={methods.control}
                    name="how"
                    label="How"
                    placeholder="How?"
                    options={howOptions}
                    onSelectChange={async (data) => {
                      setLoading(true);
                      await getMailTemplate({
                        jobOrderId: Number(jobOrderId),
                        candidateId,
                        mailTemplate: data,
                      });
                      setLoading(false);
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={3}>
                  <RHFDatePicker
                    control={methods.control}
                    name='date'
                    label='Date'
                    minDate={moment(new Date())}
                    disabled={!howValue}
                    onDateChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        '{{CLIENT_INTERVIEW_DATE}}',
                        event.format('MM/DD/YYYY'),
                      );
                      
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={3}>
                  <RHFTimePicker
                    control={methods.control}
                    name='time'
                    label='Time'
                    disabled={!howValue}
                    onTimeChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        '{{CLIENT_INTERVIEW_TIME_SLOT}}',
                        event.format('hh:mm A'),
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={3}>
                  <RHFSelect
                    control={methods.control}
                    name="timeZone"
                    label="Time Zone"
                    placeholder="Time Zone"
                    options={timeZoneOptions}
                    disabled={!howValue}
                    onSelectChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        '{{ZONE}}',
                        event,
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFSelect
                    control={methods.control}
                    name="duration"
                    label="Duration"
                    placeholder="Duration"
                    options={durationOptions}
                    disabled={!howValue}
                    onSelectChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        '{{CLIENT_DURATION}}',
                        event,
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFInput
                    control={methods.control}
                    name="where"
                    label="Where"
                    placeholder="Where"
                    disabled={!howValue}
                    onChange={(event: any) => {
                      methods.setValue('where', event);
                      getValueFromFormAndUpdateTemplate(
                        '{{CLIENT_INTERVIEW_LOCATION}}',
                        event,
                      );

                    }}
                  />
                </Grid >

                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFInput
                    control={methods.control}
                    name="who"
                    label="Who"
                    placeholder="Who"
                    disabled={!howValue}
                    onChange={(event: any) => {
                      methods.setValue('who', event);
                      getValueFromFormAndUpdateTemplate(
                        '{{CLIENT_INTERVIEWER_NAME}}',
                        event,
                      );
                    }}
                  />
                </Grid >
              </Grid >
            </div >

            <div className="w-full flex-shrink-0">
              <RHFEmailsChips
                control={methods.control}
                name="to"
                label="To"
                placeholder="Enter emails"
                disabled={!howValue}
              />
            </div>
            <div className="w-full flex-shrink-0">
              <RHFEmailsChips
                control={methods.control}
                name="cc"
                label="cc"
                placeholder="Enter emails"
                disabled={!howValue}
              />
            </div>
            <div className="w-full flex-shrink-0">
              <RHFInput
                control={methods.control}
                name="subject"
                label="Subject"
                placeholder="Subject"
                disabled={!howValue}
              />
            </div>
            <div className="w-full min-h-[200px] flex-shrink-0">
              <RHFEditor
                control={methods.control}
                name="content"
                disabled={!howValue}
              />
            </div>
          </div >
          <div className="w-full flex  gap-2 ">
            <Button variant="contained" type="submit" size="small" className="font-medium rounded-full" disabled={disableSubmit}>Send</Button>
            <Button variant="outlined" size="small" className="font-medium rounded-full" onClick={onCancel} disabled={disableSubmit}>Cancel</Button>
          </div>
          <div
            className="absolute right-2 top-2"
            onClick={onCancel}
          >
            <CloseIcon className="cursor-pointer" />
          </div>
        </form >
      </FormProvider >

      {loading && (
        <div className="fixed top-[50vh] left-[50vw]">
          <Loader />
        </div>
      )}
    </>
  );
};

ScheduleClientInterview.defaultProps = {
  title: '',
};

ScheduleClientInterview.propTypes = {

};

export default ScheduleClientInterview;
